import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authGet = (url) => axios.get(url, { headers: getAuthHeaders() });
const authPost = (url, data, config = {}) =>
  axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } });
const authPut = (url, data) =>
  axios.put(url, data, { headers: getAuthHeaders() });
const authDelete = (url) =>
  axios.delete(url, { headers: getAuthHeaders() });

function assetUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

const emptyForm = {
  title: '',
  description: '',
  theme: '',
  price: '',
  type: 'Physical',
  displayOrder: 0,
  enabled: true,
  presentationKind: 'format',
  formatId: '',
  presentationLabel: '',
  coverImage: '',
  images: [],
  designIds: []
};

function AdminBundlesPage() {
  const [bundles, setBundles] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [designSearch, setDesignSearch] = useState('');

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [bundlesRes, designsRes, formatsRes] = await Promise.all([
        authGet(`${API_BASE_URL}/admin/bundles`),
        axios.get(`${API_BASE_URL}/designs`),
        axios.get(`${API_BASE_URL}/formats`)
      ]);
      setBundles(bundlesRes.data.data || []);
      setDesigns(designsRes.data.data || []);
      const allFormats = formatsRes.data.data || [];
      setFormats(allFormats);
      setForm((prev) => (prev.formatId ? prev : { ...prev, formatId: allFormats[0]?.id || '' }));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const designsById = useMemo(
    () => Object.fromEntries(designs.map((d) => [d.id, d])),
    [designs]
  );

  const filteredDesigns = useMemo(() => {
    const q = designSearch.trim().toLowerCase();
    if (!q) return designs.slice(0, 60);
    return designs
      .filter((d) => {
        const title = (d.title || '').toLowerCase();
        const id = (d.id || '').toLowerCase();
        return title.includes(q) || id.includes(q);
      })
      .slice(0, 60);
  }, [designs, designSearch]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, formatId: formats[0]?.id || '' });
    setFormError(null);
    setDesignSearch('');
    setShowEditor(true);
  };

  const openEdit = (bundle) => {
    setEditingId(bundle.id);
    setForm({
      title: bundle.title || '',
      description: bundle.description || '',
      theme: bundle.theme || '',
      price: bundle.price ?? '',
      type: bundle.type || 'Physical',
      displayOrder: bundle.displayOrder || 0,
      enabled: bundle.enabled !== false,
      presentationKind: bundle.presentation?.kind === 'custom' ? 'custom' : 'format',
      formatId: bundle.presentation?.formatId || formats[0]?.id || '',
      presentationLabel: bundle.presentation?.label || '',
      coverImage: bundle.coverImage || '',
      images: Array.isArray(bundle.images) ? bundle.images : [],
      designIds: bundle.designIds || []
    });
    setFormError(null);
    setDesignSearch('');
    setShowEditor(true);
  };

  const openDuplicate = (bundle) => {
    setEditingId(null);
    setForm({
      title: `${bundle.title || 'Bundle'} (copy)`,
      description: bundle.description || '',
      theme: bundle.theme || '',
      price: bundle.price ?? '',
      type: bundle.type || 'Physical',
      displayOrder: bundle.displayOrder || 0,
      enabled: false,
      presentationKind: bundle.presentation?.kind === 'custom' ? 'custom' : 'format',
      formatId: bundle.presentation?.formatId || formats[0]?.id || '',
      presentationLabel: bundle.presentation?.label || '',
      coverImage: bundle.coverImage || '',
      images: Array.isArray(bundle.images) ? [...bundle.images] : [],
      designIds: Array.isArray(bundle.designIds) ? [...bundle.designIds] : []
    });
    setFormError(null);
    setDesignSearch('');
    setShowEditor(true);
  };

  const toggleDesign = (designId) => {
    setForm((prev) => {
      const has = prev.designIds.includes(designId);
      return {
        ...prev,
        designIds: has
          ? prev.designIds.filter((id) => id !== designId)
          : [...prev.designIds, designId]
      };
    });
  };

  const moveDesign = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.designIds];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, designIds: next };
    });
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      setFormError(null);
      const data = new FormData();
      data.append('cover', file);
      const response = await authPost(`${API_BASE_URL}/admin/bundles/upload-cover`, data);
      if (response.data.success) {
        setForm((prev) => ({ ...prev, coverImage: response.data.url }));
      } else {
        setFormError(response.data.error || 'Upload failed');
      }
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleGalleryUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    try {
      setUploading(true);
      setFormError(null);
      const uploaded = [];
      for (const file of files) {
        const data = new FormData();
        data.append('cover', file);
        const response = await authPost(`${API_BASE_URL}/admin/bundles/upload-cover`, data);
        if (response.data.success) uploaded.push(response.data.url);
      }
      if (uploaded.length > 0) {
        setForm((prev) => ({ ...prev, images: [...prev.images, ...uploaded] }));
      }
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const removeImage = (index) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const moveImage = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.images];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, images: next };
    });
  };

  const save = async () => {
    setFormError(null);
    if (!form.title.trim()) return setFormError('Title is required');
    if (!form.price || Number(form.price) <= 0) return setFormError('Price must be greater than 0');
    if (form.designIds.length < 2) return setFormError('Select at least 2 designs');
    if (form.presentationKind === 'format' && !form.formatId) return setFormError('Choose a format');
    if (form.presentationKind === 'custom' && !form.presentationLabel.trim()) {
      return setFormError('Enter a presentation label (e.g. "3-photo frame")');
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      theme: form.theme.trim(),
      price: Number(form.price),
      type: form.type,
      displayOrder: Number(form.displayOrder) || 0,
      enabled: !!form.enabled,
      presentation:
        form.presentationKind === 'custom'
          ? { kind: 'custom', label: form.presentationLabel.trim() }
          : { kind: 'format', formatId: form.formatId },
      coverImage: form.coverImage.trim(),
      images: form.images,
      designIds: form.designIds
    };

    try {
      setSaving(true);
      if (editingId) {
        await authPut(`${API_BASE_URL}/admin/bundles/${editingId}`, payload);
        setNotice('Bundle updated');
      } else {
        await authPost(`${API_BASE_URL}/admin/bundles`, payload);
        setNotice('Bundle created');
      }
      setShowEditor(false);
      await loadAll();
      setTimeout(() => setNotice(null), 3000);
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Failed to save bundle');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (bundle) => {
    if (!window.confirm(`Delete bundle "${bundle.title}"? This cannot be undone.`)) return;
    try {
      await authDelete(`${API_BASE_URL}/admin/bundles/${bundle.id}`);
      setNotice('Bundle deleted');
      await loadAll();
      setTimeout(() => setNotice(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete bundle');
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />
        <AdminTabs />

        {notice && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">
            {notice}
          </div>
        )}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {!showEditor && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Bundles</h2>
              <button
                onClick={openNew}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                + New Bundle
              </button>
            </div>

            {loading ? (
              <p className="text-gray-500">Loading bundles...</p>
            ) : bundles.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                No bundles yet. Create your first bundle.
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bundle</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Theme</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Presentation</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designs</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {bundles.map((bundle) => (
                      <tr key={bundle.id}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {(bundle.coverImage) && (
                              <img
                                src={assetUrl(bundle.coverImage)}
                                alt=""
                                className="w-10 h-10 rounded object-cover border border-gray-200"
                              />
                            )}
                            <div>
                              <div className="font-medium text-gray-900">{bundle.title}</div>
                              <div className="text-xs text-gray-500">{bundle.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{bundle.theme || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {bundle.presentation?.kind === 'custom'
                            ? bundle.presentation.label
                            : bundle.presentation?.formatId || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{(bundle.designIds || []).length}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                          ${Number(bundle.price).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              bundle.enabled !== false
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {bundle.enabled !== false ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                          <button
                            onClick={() => openDuplicate(bundle)}
                            className="text-gray-600 hover:text-gray-800 font-medium mr-3"
                          >
                            Duplicate
                          </button>
                          <button
                            onClick={() => openEdit(bundle)}
                            className="text-blue-600 hover:text-blue-700 font-medium mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => remove(bundle)}
                            className="text-red-600 hover:text-red-700 font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {showEditor && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingId ? 'Edit Bundle' : 'New Bundle'}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || uploading}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create bundle'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditor(false)}
                  className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>

            {formError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
                <input
                  type="text"
                  placeholder="birds, xmas, owls..."
                  value={form.theme}
                  onChange={(e) => setForm({ ...form, theme: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD) *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product type</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Physical">Physical</option>
                  <option value="Digital">Digital</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display order</label>
                <input
                  type="number"
                  value={form.displayOrder}
                  onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Presentation *</label>
                <div className="flex flex-wrap items-center gap-4 mb-2">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="presentationKind"
                      checked={form.presentationKind === 'format'}
                      onChange={() => setForm({ ...form, presentationKind: 'format' })}
                    />
                    Use an existing format
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="presentationKind"
                      checked={form.presentationKind === 'custom'}
                      onChange={() => setForm({ ...form, presentationKind: 'custom' })}
                    />
                    Custom presentation
                  </label>
                </div>
                {form.presentationKind === 'format' ? (
                  <select
                    value={form.formatId}
                    onChange={(e) => setForm({ ...form, formatId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a format…</option>
                    {formats.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name || f.id} ({f.type || 'Physical'})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder='e.g. "3-photo frame"'
                    value={form.presentationLabel}
                    onChange={(e) => setForm({ ...form, presentationLabel: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Cover image</label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    onChange={handleCoverUpload}
                    className="text-sm"
                  />
                  {uploading && <span className="text-sm text-gray-500">Uploading…</span>}
                  {form.coverImage && (
                    <img
                      src={assetUrl(form.coverImage)}
                      alt="Cover preview"
                      className="w-16 h-16 rounded object-cover border border-gray-200"
                    />
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional product images
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Shown after the cover image on the bundle page. Select one or more images.
                </p>
                <input
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={handleGalleryUpload}
                  className="text-sm"
                />
                {form.images.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {form.images.map((url, index) => (
                      <div key={`${url}-${index}`} className="relative">
                        <img
                          src={assetUrl(url)}
                          alt={`Additional ${index + 1}`}
                          className="w-20 h-20 rounded object-cover border border-gray-200"
                        />
                        <div className="absolute -bottom-2 left-0 right-0 flex justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => moveImage(index, -1)}
                            className="bg-white border border-gray-300 rounded px-1.5 text-xs text-gray-700 hover:bg-gray-100"
                            aria-label="Move left"
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            onClick={() => moveImage(index, 1)}
                            className="bg-white border border-gray-300 rounded px-1.5 text-xs text-gray-700 hover:bg-gray-100"
                            aria-label="Move right"
                          >
                            →
                          </button>
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="bg-white border border-red-200 rounded px-1.5 text-xs text-red-600 hover:bg-red-50"
                            aria-label="Remove image"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  />
                  Enabled (visible on the store)
                </label>
              </div>
            </div>

            {/* Design picker */}
            <div className="mt-6 border-t border-gray-200 pt-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Designs ({form.designIds.length} selected)
                </h3>
                <input
                  type="text"
                  placeholder="Search designs by title or id…"
                  value={designSearch}
                  onChange={(e) => setDesignSearch(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-72 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {form.designIds.length > 0 && (
                <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-600 mb-2">Selected (in order)</p>
                  <ol className="space-y-1">
                    {form.designIds.map((id, index) => (
                      <li key={id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="truncate">
                          {index + 1}. {designsById[id]?.title || id}
                        </span>
                        <span className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => moveDesign(index, -1)}
                            className="px-2 py-0.5 text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
                            aria-label="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => moveDesign(index, 1)}
                            className="px-2 py-0.5 text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
                            aria-label="Move down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleDesign(id)}
                            className="px-2 py-0.5 text-red-600 border border-red-200 rounded hover:bg-red-50"
                            aria-label="Remove"
                          >
                            ✕
                          </button>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {filteredDesigns.map((design) => {
                  const selected = form.designIds.includes(design.id);
                  return (
                    <button
                      key={design.id}
                      type="button"
                      onClick={() => toggleDesign(design.id)}
                      className={`text-left rounded-lg border p-1 transition-colors ${
                        selected
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                        <img
                          src={`${API_BASE_URL}/designs/${design.id}/thumbnail`}
                          alt={design.title}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                      <div className="mt-1 px-1">
                        <p className="text-xs font-medium text-gray-800 line-clamp-1">{design.title}</p>
                        <p className="text-[10px] text-gray-500">{design.id}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              {filteredDesigns.length === 0 && (
                <p className="text-sm text-gray-500 mt-3">No designs match your search.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminBundlesPage;
